import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  IconButton,
  Box,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import FilledButton from './FilledButton';
import OutlinedButton from './OutlinedButton';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import IconOnlyChip from './IconOnlyChip';
import api, { fetchCourses, fetchDepartments } from '../services/api';
import usePrevSubmissionDisplay from '../utils/usePrevSubmissionDisplay';
import { useAuth } from './AuthContext';
import { useInstitution } from './InstitutionContext';

const EditProfessorCourseDialog = ({ open, onClose, link: linkProp, onResubmit }) => {
  const link = linkProp ?? {};
  const { user } = useAuth();
  const { selectedInstitution } = useInstitution();
  const prevDisplay = usePrevSubmissionDisplay(link.previous_submission);
  const [courses, setCourses] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [deptId, setDeptId] = useState('');
  const [editCourse, setEditCourse] = useState(false);
  const [editProf, setEditProf] = useState(false);
  const [courseId, setCourseId] = useState('');
  const [profName, setProfName] = useState('');
  const [allProfNames, setAllProfNames] = useState([]);
  const [profDuplicate, setProfDuplicate] = useState(false);

  const toggleEditCourse = () => {
    setEditCourse((prev) => {
      if (prev) {
        setCourseId(link.course?.id || '');
        setDeptId(link.course?.department?.id || '');
      }
      return !prev;
    });
  };

  useEffect(() => {
    if (!open) return;
    setEditCourse(false);
    setEditProf(false);
    setCourseId(link.course?.id || '');
    setProfName('');
    setProfDuplicate(false);
    const instId = link.course?.department?.institution?.id;
    setDeptId(link.course?.department?.id || '');
    if (instId) {
      fetchDepartments(instId)
        .then((res) => setDepartments(res || []))
        .catch(() => setDepartments([]));
      fetchCourses(instId)
        .then((res) => {
          const list = res.results || res;
          setAllCourses(list || []);
          const currentDept = link.course?.department?.id;
          setCourses(
            list.filter((c) => c.department?.id === currentDept)
          );
        })
        .catch(() => {
          setAllCourses([]);
          setCourses([]);
        });
      const nameInst =
        link.course?.department?.institution?.id ||
        user?.userprofile?.institution?.id ||
        selectedInstitution;
      api
        .get('/courses/professor-names/', {
          params: { institution_id: nameInst },
        })
        .then((res) => setAllProfNames(res.data || []))
        .catch(() => setAllProfNames([]));
    } else {
      setDepartments([]);
      setAllCourses([]);
      setCourses([]);
      setAllProfNames([]);
    }
  }, [open, link, user, selectedInstitution]);

  useEffect(() => {
    if (!deptId) {
      setCourses(allCourses);
    } else {
      setCourses(allCourses.filter((c) => c.department?.id === deptId));
    }
  }, [deptId, allCourses]);

  useEffect(() => {
    if (!editProf) {
      setProfDuplicate(false);
      return;
    }
    const dup = allProfNames.some(
      (n) => n.trim().toLowerCase() === profName.trim().toLowerCase()
    );
    setProfDuplicate(dup);
  }, [profName, allProfNames, editProf]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!linkProp) return;
    const payload = {};
    if (editCourse) payload.course = courseId || link.course?.id;
    if (editProf && link.professor_submission) {
      payload.professor_name = profName || link.professor_submission?.name;
    }
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    if (onResubmit) onResubmit(link.id, payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ component: 'form', onSubmit: handleSubmit }}
    >
      <DialogTitle>Edit Rejected Professor</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Department
          </Typography>
          <Typography>{link.course?.department?.name || '—'}</Typography>
          {editCourse && (
            <FormControl size="small" sx={{ minWidth: 180, mt: 1 }}>
              <Select value={deptId} onChange={(e) => setDeptId(e.target.value)}>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Current Course Selection
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>
              {link.course ? `${link.course.short_name} – ${link.course.long_name}` : '—'}
            </Typography>
            <IconOnlyChip icon={<EditIcon fontSize="small" />} onClick={toggleEditCourse} />
          </Box>
          {editCourse && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select value={courseId} onChange={(e) => setCourseId(e.target.value)}>
                  {courses.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.short_name} – {c.long_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconOnlyChip
                icon={<CancelIcon fontSize="small" />}
                onClick={toggleEditCourse}
              />
            </Box>
          )}
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Professor
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>{link.professor?.name || link.professor_submission?.name || '—'}</Typography>
            {editProf && link.professor_submission ? (
              <>
                <TextField
                  size="small"
                  value={profName}
                  onChange={(e) => setProfName(e.target.value)}
                  placeholder="New Professor Name"
                  error={profDuplicate}
                  helperText={profDuplicate ? 'Professor already exists' : ''}
                />
                <IconOnlyChip
                  icon={<CancelIcon fontSize="small" />}
                  onClick={() => {
                    setEditProf(false);
                    setProfName('');
                    setProfDuplicate(false);
                  }}
                />
              </>
            ) : (
              link.professor_submission && (
                <IconOnlyChip
                  icon={<EditIcon fontSize="small" />}
                  onClick={() => setEditProf(true)}
                />
              )
            )}
          </Box>
        </Box>
        {link.rejection_reason && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Reason for Rejection
            </Typography>
            <Typography>{link.rejection_reason}</Typography>
          </Box>
        )}
        {link.previous_submission && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Previous Submission
            </Typography>
            <Typography>{prevDisplay}</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <OutlinedButton onClick={onClose} text="Cancel" color="info" />
        <FilledButton
          type="submit"
          text="Resubmit"
          color="info"
        />
      </DialogActions>
    </Dialog>
  );
};

export default EditProfessorCourseDialog;
